import { motion } from "framer-motion";
import {
  Sparkles,
  GraduationCap,
  Video,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Users,
} from "lucide-react";
import MockFrame from "./MockFrame";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stages = [
  { label: "Applied", value: 247, pct: 100 },
  { label: "Screened", value: 89, pct: 36 },
  { label: "Interviewed", value: 23, pct: 9 },
  { label: "Offered", value: 7, pct: 3 },
];

const candidates = [
  { initials: "EM", name: "Elena Morales", role: "Senior Platform Engineer", uni: "Stanford", match: 96 },
  { initials: "RK", name: "Rohan Krishnan", role: "ML Engineer", uni: "MIT", match: 91 },
  { initials: "NF", name: "Nia Ferreira", role: "Product Designer", uni: "RISD", match: 87 },
];

const interviews = [
  { time: "10:00", with: "Elena M.", round: "Tech screen", icon: Video },
  { time: "11:30", with: "Rohan K.", round: "System design", icon: Briefcase },
  { time: "14:00", with: "Nia F.", round: "Portfolio review", icon: GraduationCap },
];

function MatchRing({ pct, accent }) {
  const r = 13;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
      <svg viewBox="0 0 32 32" className="absolute inset-0 -rotate-90" aria-hidden>
        <circle cx="16" cy="16" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="none" />
        <motion.circle
          cx="16"
          cy="16"
          r={r}
          stroke={accent.primary}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: c * (1 - pct / 100) }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          style={{ filter: `drop-shadow(0 0 4px ${accent.glow})` }}
        />
      </svg>
      <span
        className="relative text-[9.5px] font-bold tabular-nums"
        style={{ color: accent.light }}
      >
        {pct}
      </span>
    </div>
  );
}

export default function RecroidVisual({ accent }) {
  return (
    <MockFrame
      domain="recroid.velozent.com / pipeline"
      accent={accent}
      tabs={["Pipeline", "Candidates", "Interviews", "Universities"]}
      activeTab={0}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="space-y-3"
      >
        <motion.div
          variants={fadeUp}
          custom={0}
          className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
              Hiring funnel · Senior PE
            </p>
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold"
              style={{ color: accent.light }}
            >
              <Users className="h-3 w-3" />
              247 candidates
            </span>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-1.5">
            {stages.map((s, i) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                custom={i + 1}
                className="relative"
              >
                <div className="flex items-baseline justify-between gap-1">
                  <p className="truncate text-[9.5px] font-semibold text-white/55">
                    {s.label}
                  </p>
                  <p
                    className="text-[14px] font-bold tabular-nums"
                    style={{ color: accent.light }}
                  >
                    {s.value}
                  </p>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/[0.04]">
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${accent.deep}, ${accent.primary})`,
                      boxShadow: `0 0 6px ${accent.glow}`,
                    }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${s.pct}%` }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{
                      duration: 0.9,
                      delay: 0.35 + i * 0.1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />
                </div>
                {i < stages.length - 1 && (
                  <ChevronRight
                    className="absolute -right-1 top-0 hidden h-3 w-3 text-white/15 sm:block"
                    aria-hidden
                  />
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-5">
          <motion.div
            variants={fadeUp}
            custom={5}
            className="rounded-xl border border-white/5 bg-white/[0.025] lg:col-span-3"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                AI-ranked candidates
              </p>
              <span
                className="inline-flex items-center gap-1 text-[9.5px] font-bold"
                style={{ color: accent.light }}
              >
                <Sparkles className="h-2.5 w-2.5" />
                Auto-scored
              </span>
            </div>
            <ul className="divide-y divide-white/5">
              {candidates.map((c, i) => (
                <motion.li
                  key={c.name}
                  variants={fadeUp}
                  custom={6 + i}
                  className="flex items-center gap-2.5 px-3 py-2"
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[9.5px] font-bold ring-1"
                    style={{
                      backgroundColor: accent.bg,
                      color: accent.light,
                      borderColor: accent.border,
                    }}
                  >
                    {c.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-semibold text-white/90">
                      {c.name}
                    </p>
                    <p className="truncate text-[9.5px] text-white/45">
                      {c.role} ·{" "}
                      <span style={{ color: accent.light }}>{c.uni}</span>
                    </p>
                  </div>
                  <MatchRing pct={c.match} accent={accent} />
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={9}
            className="rounded-xl border border-white/5 bg-white/[0.025] lg:col-span-2"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Today
              </p>
              <span className="text-[9.5px] font-medium text-white/35">
                3 interviews
              </span>
            </div>
            <ul className="divide-y divide-white/5">
              {interviews.map((iv, i) => {
                const IvIcon = iv.icon;
                return (
                  <motion.li
                    key={iv.time}
                    variants={fadeUp}
                    custom={10 + i}
                    className="flex items-center gap-2 px-3 py-2"
                  >
                    <span
                      className="text-[10px] font-bold tabular-nums"
                      style={{ color: accent.light }}
                    >
                      {iv.time}
                    </span>
                    <span
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
                      style={{
                        backgroundColor: accent.bg,
                        color: accent.primary,
                      }}
                    >
                      <IvIcon className="h-2.5 w-2.5" strokeWidth={2.4} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[10.5px] font-semibold text-white/85">
                        {iv.with}
                      </p>
                      <p className="truncate text-[9px] text-white/45">{iv.round}</p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={13}
          className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" style={{ color: accent.primary }} />
            <p className="text-[10.5px] text-white/65">
              <span className="font-semibold text-white/85">University portal:</span>{" "}
              <span style={{ color: accent.light }}>42 placements</span> sourced from 6
              partner universities this quarter
            </p>
          </div>
          <span className="hidden text-[9.5px] font-medium text-white/35 sm:inline">
            Updated 5m ago
          </span>
        </motion.div>
      </motion.div>
    </MockFrame>
  );
}
